import { useEffect } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import queryString from 'query-string';
import classNames from 'classnames';
import { t } from 'ttag';

import { PostKind } from '@v5Models';
import { socialProductFeed } from '@urls';
import SlickSlider from '@ui/SlickSlider';
import { SliderArrow, SliderArrowProps } from '@ui/SliderArrow';
import { Link } from '@ui/Link';
import { Title } from '@ui/Title';
import { Skeleton } from '@ui/Skeleton';
import { ProductDetailsPostCard } from '@components/ProductDetailsPostCard';

import s from './ProductDetailsPosts.sss';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ArrowLeft: React.FC<SliderArrowProps> = ({ className, ...props }) => (
  <SliderArrow
    {...props}
    direction="left"
    overrideClassName={s.arrow}
  />
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ArrowRight: React.FC<SliderArrowProps> = ({ className, ...props }) => (
  <SliderArrow
    {...props}
    overrideClassName={s.arrow}
  />
);

interface SocialPostAuthor {
  id: number,
  username: string,
  avatar: string,
  isSubscribed: boolean,
  followersCount: number
}

interface SocialPostProduct {
  id: number,
  brandId?: number | null,
  url: string,
  name: string,
  thumbnail: string,
  slug: string,
  price: number,
  catalogId: number,
  brand?: {
    id: number,
    name: string
  } | null
}

interface SocialPost {
  id: string,
  isLiked: boolean,
  description: string,
  likesCount: number,
  commentsCount: number,
  productsIds: string[],
  tags: string[],
  mediasUrls: string[],
  type: PostKind,
  author: SocialPostAuthor,
  products: SocialPostProduct[],
}

interface Props {
  className?: string,
  productId: string,
  posts: SocialPost[],
  loading: boolean,
  fetching: boolean,
  hasNextPage: boolean,
  onLoadMore: () => void,
}

export const ProductDetailsPosts: React.FC<Props> = ({
  className,
  productId,
  posts,
  loading,
  fetching,
  hasNextPage,
  onLoadMore,
}) => {
  const history = useHistory();

  const { ref, inView } = useInView();

  const getSocialProductFeedUrl = (postId: string) => {
    const socialProductFeedPathname = generatePath(socialProductFeed, {
      productId,
    });
  
    return history.createHref({
      pathname: socialProductFeedPathname,
      search: queryString.stringify({
        postId,
        referrer: 'product_card',
      }),
    });
  };

  useEffect(() => {
    if (inView) {
      onLoadMore();
    }
  }, [inView, onLoadMore]);

  if (!loading && !posts.length) {
    return null;
  }

  return (
    <div className={classNames(s.root, className)}>
      <Title
        className={s.title}
        type="h6"
        bold={false}
      >
        {t`Огляди товару`}
      </Title>

      <SlickSlider
        className={s.carousel}
        speed={400}
        prevArrow={<ArrowLeft />}
        nextArrow={<ArrowRight />}
        slidesToShow={4}
        infinite={false}
        arrows={posts.length > 4}
        responsive={[
          {
            breakpoint: 768,
            settings: {
              slidesToShow: 2.2,
            },
          },
          {
            breakpoint: 1024,
            settings: {
              slidesToShow: 3,
            },
          },
        ]}
        variableWidth={false}
        swipeToSlide={true}
      >
        
        {loading ? (
          Array(10).fill(null).map((_, i) => (
            <div
              key={i}
              className={s.container}
            >
              <Skeleton
                width="100%"
                height="300px"
              />
            </div>
          ))
        ) : (
          posts.map((post) => (
            <Link
              key={post.id}
              className={s.container}
              theme="clean"
              href={getSocialProductFeedUrl(post.id)}
            >
              <ProductDetailsPostCard post={post} />
            </Link>
          ))
        )}

        {!fetching && hasNextPage && (
          Array(3).fill(null).map((_, i) => (
            <div
              key={i}
              ref={i === 0 ? ref : undefined}
              className={s.container}
            >
              <Skeleton
                width="100%"
                height="100%"
              />
            </div>
          ))
        )}
      </SlickSlider>
    </div>
  );
};
